import { NetworkError } from '@/api/api';
import ErrorTitle from '@/components/errorTitle';
import { notifications, type NotificationData } from '@mantine/notifications';
import { IconExclamationCircle } from '@tabler/icons-react';
import { ServerError } from 'common/types/errors';
import type { User } from 'common/types/user';
import { calcDuration } from 'cross-state';
import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslator } from 'translations';
import z, { ZodError } from 'zod';

export interface ErrorOptions {
    title?: string;
    user?: User;
}

export interface HandleErrorOptions
    extends ErrorOptions, Omit<Partial<NotificationData>, 'title'> {}

let seenErrors = new Array<unknown>();

export function errorToString(error: unknown, { includeStack }: { includeStack?: boolean } = {}) {
    if (error instanceof Error) {
        if (includeStack) {
            return error.stack ?? error.message;
        }

        return error.message;
    }

    if (typeof error === 'string') {
        return error;
    }

    return JSON.stringify(error, null, 2);
}

export function handleError(_error: unknown, options?: HandleErrorOptions): string | undefined {
    const error = _error instanceof Error ? _error : new Error(errorToString(_error));

    if (seenErrors.includes(error)) {
        return;
    }
    seenErrors = [...seenErrors, error].slice(-10);

    console.error(...[options?.title].filter(Boolean), error);

    const { title, message } = matchError(error);
    const errorDetails = getErrorDetails(error, options);

    return (options?.id ? notifications.update : notifications.show)({
        color: 'red',
        icon: <IconExclamationCircle />,
        title: <ErrorTitle error={errorDetails}>{options?.title ?? title}</ErrorTitle>,
        message: message && (
            <div
                css={{
                    maxHeight: '5em',
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                }}
            >
                {message}
            </div>
        ),
        withBorder: true,
        autoClose: calcDuration({ seconds: 10 }),
        ...options,
    });
}

export function getErrorDetails(error: unknown, { user, title }: ErrorOptions = {}) {
    return [
        `Time: ${new Date().toISOString()}`,
        `URL: ${window.location.href}`,
        `User agent: ${navigator.userAgent}`,
        `Version: ${import.meta.env.VERSION || '0.0.0'}`,
        user && `User: ${user.mail}`,
        '---',
        title,
        errorToString(error, { includeStack: true }),
        error instanceof ServerError &&
            '\n' +
                Object.entries(error)
                    .map(([key, value]) => `${key}: ${value}`)
                    .join('\n'),
    ]
        .filter(Boolean)
        .join('\n');
}

export function matchError(
    error: unknown,
    { title }: { title?: ReactNode } = {},
): { title: ReactNode; message: ReactNode } {
    if (title !== undefined) {
        return {
            title,
            message: matchError(error).message,
        };
    }

    if (error instanceof ServerError) {
        return {
            title: error.message,
            message: <RenderServerError error={error} />,
        };
    }

    if (error instanceof ZodError) {
        return {
            title: 'Validation Error',
            message: z.prettifyError(error),
        };
    }

    if (error instanceof NetworkError) {
        return {
            title: 'Network Error',
            message: `Could not fetch ${error.url}. Please check your network connection.`,
        };
    }

    return {
        title: 'An error occurred',
        message: errorToString(error),
    };
}

function RenderServerError({ error }: { error: ServerError }) {
    const t = useTranslator();

    return t.dynamic(
        `errors.errorCode.${error.errorCode}`,
        {},
        {
            fallback: t.dynamic(
                `errors.statusCode.${error.statusCode}`,
                {},
                {
                    fallback: t('errors.genericServerError', { statusCode: error.statusCode }),
                },
            ),
        },
    );
}

export function useHandleError(errors: unknown, options?: HandleErrorOptions) {
    const error = (Array.isArray(errors) ? errors : [errors]).find(Boolean);
    const user = undefined;
    const notificationId = useRef<string>(undefined);

    useEffect(() => {
        if (!error) {
            if (notificationId.current) {
                notifications.hide(notificationId.current);
                notificationId.current = undefined;
            }
            return;
        }

        notificationId.current = handleError(error, {
            user,
            id: notificationId.current,
            ...options,
        });
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);

    useEffect(
        () => () => {
            if (notificationId.current) {
                notifications.hide(notificationId.current);
            }
        },
        [],
    );
}
